import { SEOHead } from "@/components/SEOHead";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, DollarSign, TrendingUp, ShieldCheck, BarChart3, Clock, CheckCircle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";

const partners = [
  { name: "Mastercard", logo: "https://static.wixstatic.com/media/5c8a3f_1a5f8df1f9244a2594abc519de13cb06~mv2.png/v1/fill/w_170,h_170,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/EXPANDING%20FUEL%20NETWORK%20Experience%20unparalleled%20convenience%20with%20our%20fuel%20card%20network%2C%20acc.png" },
  { name: "WEX", logo: "https://static.wixstatic.com/media/5c8a3f_b3908a79b75141b8a704a365037185e9~mv2.png/v1/fill/w_170,h_170,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/EXPANDING%20FUEL%20NETWORK%20Experience%20unparalleled%20convenience%20with%20our%20fuel%20card%20network%2C%20acc.png" },
  { name: "Partner 3", logo: "https://static.wixstatic.com/media/5c8a3f_2d832770b547407b90bf976d46309527~mv2.png/v1/fill/w_170,h_170,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/EXPANDING%20FUEL%20NETWORK%20Experience%20unparalleled%20convenience%20with%20our%20fuel%20card%20network%2C%20acc.png" },
  { name: "Volvo", logo: "https://static.wixstatic.com/media/5c8a3f_ab7b5a983d9e463e8139bd8a9e2d78e8~mv2.jpg/v1/fill/w_170,h_170,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/286708_Volvo_Iron_Mark.jpg" },
  { name: "RAM", logo: "https://static.wixstatic.com/media/5c8a3f_363b557ef5b94cf4a819271c556bd1e1~mv2.png/v1/fill/w_170,h_170,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/585acc014f6ae202fedf294f.png" },
];

const features = [
  {
    icon: Clock,
    title: "Stay Cash Flow Ready",
    titleRu: "Готовность к денежному потоку",
    desc: "Our factoring services ensure you get paid faster, improving cash flow and reducing financial stress. Focus on growth while we handle the payments.",
    descRu: "Наши факторинговые услуги гарантируют более быструю оплату, улучшая денежный поток и снижая финансовый стресс. Сосредоточьтесь на росте, пока мы занимаемся платежами.",
  },
  {
    icon: BarChart3,
    title: "Effortless Accounting",
    titleRu: "Простая бухгалтерия",
    desc: "Leave the bookkeeping and number-crunching to us! Our expert accounting services keep your finances organized and compliant, saving you time and hassle.",
    descRu: "Оставьте бухгалтерию и подсчёты нам! Наши экспертные бухгалтерские услуги сохраняют ваши финансы организованными и соответствующими требованиям, экономя ваше время.",
  },
  {
    icon: TrendingUp,
    title: "Streamline Your Finances",
    titleRu: "Оптимизация финансов",
    desc: "With our accounting and factoring solutions, managing your business's finances has never been easier. We simplify the process so you can focus on what matters.",
    descRu: "С нашими бухгалтерскими и факторинговыми решениями управление финансами вашего бизнеса ещё никогда не было таким простым. Мы упрощаем процесс, чтобы вы могли сосредоточиться на главном.",
  },
  {
    icon: ShieldCheck,
    title: "Reduce Financial Risks",
    titleRu: "Снижение финансовых рисков",
    desc: "Our services offer reliable cash flow and professional financial management, helping you minimize risks and maintain stability as your business grows.",
    descRu: "Наши услуги обеспечивают надёжный денежный поток и профессиональное финансовое управление, помогая минимизировать риски и поддерживать стабильность по мере роста вашего бизнеса.",
  },
];

const benefits = [
  { en: "Same-day or next-day funding available", ru: "Финансирование в тот же или на следующий день" },
  { en: "No long-term contracts required", ru: "Долгосрочные контракты не требуются" },
  { en: "Online account management 24/7", ru: "Управление аккаунтом онлайн 24/7" },
  { en: "Dedicated account manager", ru: "Персональный менеджер по счету" },
  { en: "Fuel card integration available", ru: "Доступна интеграция с топливными картами" },
  { en: "Free credit checks on brokers", ru: "Бесплатная проверка кредитоспособности брокеров" },
];

const factoringSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Accounting & Factoring",
  "provider": {
    "@type": "LocalBusiness",
    "name": "AITIM Group"
  },
  "areaServed": "US",
  "description": "Complete financial solutions for trucking businesses, including factoring, accounting, and cash flow management."
};

const Factoring = () => {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead 
        title={language === 'ru' ? 'Бухгалтерия & Факторинг' : 'Accounting & Factoring'}
        description={language === 'ru'
          ? '"Вы занимаетесь дорогой, мы занимаемся числами!"'
          : '"You Focus on the Road, We Handle the Numbers!"'}
        canonical="/factoring"
        schemaJson={factoringSchema}
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative pt-28 pb-16 lg:pt-48 lg:pb-32 overflow-hidden bg-navy-950">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-950"></div>
            <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
            <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4"></div>
            {/* Animated Grid Background */}
            <div className="absolute inset-0 opacity-[0.15]" style={{ backgroundImage: 'linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary font-semibold text-sm mb-6 border border-primary/30 tracking-wide uppercase">
                {language === 'ru' ? 'Финансовые услуги' : 'Financial Excellence'}
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-7xl font-black text-white mb-6 leading-tight tracking-tight">
                {language === 'ru' ? 'Бухгалтерия &' : 'Accounting &'}{' '}
                <span className="text-primary">{language === 'ru' ? 'Факторинг' : 'Factoring'}</span>
              </h1>
              <p className="text-xl lg:text-2xl text-white/80 mb-10 max-w-3xl mx-auto leading-relaxed italic font-medium">
                {language === 'ru'
                  ? '"Вы занимаетесь дорогой, мы занимаемся числами!"'
                  : '"You Focus on the Road, We Handle the Numbers!"'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/free-consultation">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-sm px-8 h-14 text-lg group">
                    {language === 'ru' ? 'Получить расчет' : 'Get a Free Quote'} <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/free-consultation">
                  <Button size="lg" variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-sm px-8 h-14 text-lg">
                    {language === 'ru' ? 'Связаться с нами' : 'Contact Us'}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <div className="text-primary font-bold tracking-widest uppercase text-xs mb-4">
                {language === 'ru' ? 'НАШИ УСЛУГИ' : 'OUR SERVICES'}
              </div>
              <h2 className="text-4xl font-bold text-navy-950">
                {language === 'ru' ? 'Комплексные финансовые решения' : 'Complete Financial Solutions'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {features.map((f, i) => (
                <div key={i} className="group bg-white rounded-2xl p-10 shadow-sm border border-slate-100 hover:-translate-y-2 hover:shadow-2xl hover:shadow-primary/10 hover:border-primary/20 transition-all duration-500 flex gap-6 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="shrink-0 w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                    <f.icon className="w-7 h-7 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-navy-950 mb-3">
                      {language === 'ru' ? f.titleRu : f.title}
                    </h3>
                    <p className="text-slate-500 leading-relaxed">
                      {language === 'ru' ? f.descRu : f.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quote Banner */}
        <section className="py-20 bg-navy-950 text-white text-center">
          <div className="container mx-auto px-4 max-w-3xl">
            <DollarSign className="w-14 h-14 text-primary mx-auto mb-6 opacity-80" strokeWidth={1.5} />
            <blockquote className="text-2xl lg:text-3xl font-bold italic mb-8 leading-relaxed">
              {language === 'ru'
                ? '"Упрощайте финансы, усиливайте бизнес."'
                : '"Simplify Your Finances, Amplify Your Business."'}
            </blockquote>
            <Link to="/free-consultation">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-sm px-8 h-12" aria-label={language === 'ru' ? 'Узнать больше о факторинге' : 'Learn More about Factoring'}>
                {language === 'ru' ? 'Узнать больше' : 'Learn More'} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Benefits List */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="text-primary font-bold tracking-widest uppercase text-xs mb-4">
                  {language === 'ru' ? 'ПОЧЕМУ МЫ' : 'WHY CHOOSE US'}
                </div>
                <h2 className="text-4xl font-bold text-navy-950 mb-6">
                  {language === 'ru' ? 'Преимущества работы с нами' : 'Benefits of Working With Us'}
                </h2>
                <p className="text-slate-500 mb-10 leading-relaxed text-lg">
                  {language === 'ru'
                    ? 'Мы предоставляем комплексные финансовые решения, специально разработанные для транспортных компаний. Наши услуги помогают вам оставаться платёжеспособными и сосредоточиться на развитии бизнеса.'
                    : 'We provide comprehensive financial solutions specifically designed for trucking companies. Our services help you stay solvent and focus on growing your business.'}
                </p>
                <ul className="space-y-4">
                  {benefits.map((b, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                      <span className="text-slate-700 font-medium">{language === 'ru' ? b.ru : b.en}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-10">
                  <Link to="/free-consultation">
                    <Button className="bg-primary hover:bg-primary/90 text-white rounded-sm px-8 h-12">
                      {language === 'ru' ? 'Начать сейчас' : 'Get Started'} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-6">
                {[
                  { num: "24h", label: language === 'ru' ? 'Быстрое финансирование' : 'Fast Funding', sub: language === 'ru' ? 'В тот же или на следующий день' : 'Same or next day' },
                  { num: "98%", label: language === 'ru' ? 'Удовлетворённость клиентов' : 'Client Satisfaction', sub: language === 'ru' ? 'Рейтинг наших клиентов' : 'From our clients' },
                  { num: "$50M+", label: language === 'ru' ? 'Обработано платежей' : 'Payments Processed', sub: language === 'ru' ? 'Ежегодно' : 'Annually' },
                  { num: "10+", label: language === 'ru' ? 'Лет опыта' : 'Years Experience', sub: language === 'ru' ? 'В финансовых услугах' : 'In financial services' },
                ].map((s, i) => (
                  <div key={i} className="bg-navy-950 rounded-2xl p-8 text-center">
                    <div className="text-3xl font-black text-primary mb-2">{s.num}</div>
                    <div className="text-white font-bold text-sm mb-1">{s.label}</div>
                    <div className="text-white/50 text-xs">{s.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Biggest Network */}
        <section className="py-20 bg-slate-50 overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="text-primary font-bold tracking-widest uppercase text-xs mb-4">
                  {language === 'ru' ? 'НАША СЕТЬ' : 'OUR NETWORK'}
                </div>
                <h2 className="text-4xl lg:text-5xl font-black text-navy-950 mb-8 tracking-tight">
                  {language === 'ru' ? 'КРУПНЕЙШАЯ СЕТЬ' : 'BIGGEST NETWORK'}
                </h2>
                <div className="grid grid-cols-3 gap-6">
                  {partners.map((p, i) => (
                    <div key={i} className="bg-white rounded-xl p-4 flex items-center justify-center h-24 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                      <img src={p.logo} alt={p.name} className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 transition-all" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden h-[480px]">
                <img
                  src="https://static.wixstatic.com/media/5c8a3f_7d3c3b2d0ecd4bf491d4f8973a113c94~mv2.png/v1/fill/w_962,h_723,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/5c8a3f_7d3c3b2d0ecd4bf491d4f8973a113c94~mv2.png"
                  alt="AITIM Group App"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-navy-950 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold mb-4">
              {language === 'ru' ? 'Готовы улучшить денежный поток?' : 'Ready to Improve Your Cash Flow?'}
            </h2>
            <p className="text-white/70 mb-8 max-w-xl mx-auto text-lg">
              {language === 'ru'
                ? 'Свяжитесь с нами сегодня и узнайте, как наши бухгалтерские и факторинговые услуги могут трансформировать ваш бизнес.'
                : 'Contact us today and discover how our accounting and factoring services can transform your trucking business.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/free-consultation">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-sm px-8 h-14 text-lg">
                  {language === 'ru' ? 'Бесплатная консультация' : 'Free Consultation'} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:7739809090" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-sm px-8 h-14 text-lg w-full">
                  {language === 'ru' ? 'Позвонить: 773-980-9090' : 'Call: 773-980-9090'}
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Factoring;
